import { FC, useState } from 'react';
import { message, Tabs } from 'antd';
import { IdcardOutlined, ScanOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Card } from '../../../../../components/card/card';
import { API } from '../../../../../apis';
import { postViaAuth, responseValidator } from '../../../../../apis/api';
import {
    AuthenticationServiceSettings,
    AuthenticationServicesSettingsDto,
    BiometricSettings,
} from '../../../../../core/domain';
import { SettingsSection, SwitchField, TextInput } from '../shared/settings-ui';
import { useLoadSettings } from '../shared/use-settings';
import '../settings.scss';

/* ---------------- سرویس‌های احراز هویت ---------------- */
const AuthServicesTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<AuthenticationServicesSettingsDto>(
        API.siteSettings.authServiceSettings,
        setCardLoading,
    );
    const [saving, setSaving] = useState(false);

    const services = data?.authenticationServices ?? [];

    const patch = (index: number, changes: Partial<AuthenticationServiceSettings>) => {
        const next = services.map((s, i) => (i === index ? { ...s, ...changes } : s));
        setData({ authenticationServices: next });
    };

    // Only one auth service may be active at a time.
    const setActive = (index: number, isActive: boolean) => {
        const next = services.map((s, i) => ({ ...s, isActive: i === index ? isActive : false }));
        setData({ authenticationServices: next });
    };

    const save = async () => {
        setSaving(true);
        const res: any = await postViaAuth(API.siteSettings.editAuthServices, {
            authenticationServicesSettings: { authenticationServices: services },
        });
        setSaving(false);
        if (responseValidator(res.status)) message.success('سرویس‌های احراز هویت ذخیره شد.');
    };

    return (
        <SettingsSection
            title="سرویس‌های احراز هویت"
            description="سرویس فعال برای احراز هویت کاربران را انتخاب و کلیدهای آن را تنظیم کنید. تنها یک سرویس می‌تواند فعال باشد."
            onSave={save}
            loading={saving}
        >
            {services.length === 0 && <p style={{ color: '#7a869a' }}>سرویسی برای نمایش وجود ندارد.</p>}
            {services.map((service, index) => (
                <div className={`gateway-card settings-col-span-2 ${service.isActive ? 'is-active' : ''}`} key={service.name ?? index}>
                    <div className="gateway-card__head">
                        <h4>{service.name}</h4>
                        <SwitchField
                            label="سرویس فعال"
                            checked={service.isActive}
                            onChange={(c) => setActive(index, c)}
                        />
                    </div>
                    <div className="settings-grid">
                        <TextInput
                            label="API Key"
                            value={service.apiKey}
                            onChange={(v) => patch(index, { apiKey: v })}
                        />
                        <TextInput
                            label="Secret Key"
                            value={service.secretKey}
                            onChange={(v) => patch(index, { secretKey: v })}
                        />
                        <TextInput label="Token" value={service.token} onChange={(v) => patch(index, { token: v })} />
                        <TextInput
                            label="Biometric Token"
                            value={service.biometricSettingsToken}
                            onChange={(v) => patch(index, { biometricSettingsToken: v })}
                        />
                    </div>
                </div>
            ))}
        </SettingsSection>
    );
};

/* ---------------- احراز هویت بایومتریک ---------------- */
const BiometricTab: FC<{ setCardLoading: (b: boolean) => void }> = ({ setCardLoading }) => {
    const { data, setData } = useLoadSettings<BiometricSettings>(API.siteSettings.biometricSettings, setCardLoading);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const res: any = await postViaAuth(API.siteSettings.updateBiometricSettings, {
            manualAuthenticate: !!data?.manualAuthenticate,
            biometricAuthenticate: !!data?.biometricAuthenticate,
            words: data?.words ?? '',
            line: data?.line ?? '',
        });
        setSaving(false);
        if (responseValidator(res.status)) message.success('تنظیمات احراز هویت بایومتریک ذخیره شد.');
    };

    return (
        <SettingsSection
            title="احراز هویت بایومتریک"
            description="روش‌های مجاز احراز هویت و متن جمله‌ای که کاربر هنگام ضبط ویدیو باید بخواند."
            onSave={save}
            loading={saving}
        >
            <div className="settings-note settings-col-span-2">
                <InfoCircleOutlined />
                <span>
                    در متن جمله می‌توانید از <b>%firstName</b> و <b>%lastName</b> استفاده کنید؛ این عبارت‌ها هنگام نمایش
                    با نام کاربر جایگزین می‌شوند.
                </span>
            </div>
            <div className="settings-col-span-2">
                <SwitchField
                    label="احراز هویت دستی"
                    hint="بررسی مدارک کاربر توسط ادمین"
                    checked={data?.manualAuthenticate}
                    onChange={(c) => setData({ ...data, manualAuthenticate: c } as BiometricSettings)}
                />
            </div>
            <div className="settings-col-span-2">
                <SwitchField
                    label="احراز هویت بایومتریک"
                    hint="تشخیص چهره / ویدیوی زنده"
                    checked={data?.biometricAuthenticate}
                    onChange={(c) => setData({ ...data, biometricAuthenticate: c } as BiometricSettings)}
                />
            </div>
            <div className="settings-col-span-2">
                <TextInput
                    label="جمله احراز هویت"
                    dir="rtl"
                    value={data?.line}
                    placeholder="جمله‌ای که کاربر باید بخواند را وارد کنید"
                    onChange={(v) => setData({ ...data, line: v } as BiometricSettings)}
                />
            </div>
            <div className="settings-col-span-2">
                <TextInput
                    label="کلمات کلیدی (Words)"
                    dir="rtl"
                    value={data?.words}
                    placeholder="کلمات کلیدی مورد استفاده در بررسی"
                    onChange={(v) => setData({ ...data, words: v } as BiometricSettings)}
                />
            </div>
        </SettingsSection>
    );
};

export const AuthenticationSettings: FC = () => {
    const [cardLoading, setCardLoading] = useState(false);

    const items = [
        {
            key: 'auth-services',
            label: 'سرویس‌های احراز هویت',
            icon: <IdcardOutlined />,
            children: <AuthServicesTab setCardLoading={setCardLoading} />,
        },
        {
            key: 'biometric',
            label: 'بایومتریک',
            icon: <ScanOutlined />,
            children: <BiometricTab setCardLoading={setCardLoading} />,
        },
    ];

    return (
        <Card blur={cardLoading} className="pmx-settings-page">
            <Tabs items={items} destroyInactiveTabPane />
        </Card>
    );
};
